import {
  BRAND_WIZARD_STEP_COUNT,
  BRAND_WIZARD_STEPS,
  type AudienceFieldKey,
  type BrandFieldKey,
  type BrandWizardStep,
} from '@mg/shared';
import { SECTION_COPY, WIZARD_COPY } from '../brand/brandCopy';

/**
 * Brand Setup's eight steps (spec:76-87; web.md §4.2): what each one is called and says. The fields each
 * step saves come from the shared BRAND_WIZARD_STEPS (draft/brandDraft.ts `stepFieldKeys`).
 */

/** First-time setup, or a revisit of a finished Brand Setup (fixed when the wizard opens). */
export type WizardMode = 'setup' | 'revisit';

/** One field the wizard shows: a brand field, or a field of one audience card. Release 4's hooks take it. */
export type WizardFieldRef =
  | { kind: 'field'; key: BrandFieldKey }
  | { kind: 'audience'; card: string; key: AudienceFieldKey };

export const WIZARD_STEPS: readonly BrandWizardStep[] = BRAND_WIZARD_STEPS.map((s) => s.step);

/** Every step: in a revisit every pill shows done. */
export const ALL_STEPS_DONE: readonly BrandWizardStep[] = WIZARD_STEPS;

export function isLastStep(step: BrandWizardStep): boolean {
  return step === BRAND_WIZARD_STEP_COUNT;
}

/** The step after `step` (step 8 has none: it finishes). */
export function stepAfter(step: BrandWizardStep): BrandWizardStep {
  return Math.min(step + 1, BRAND_WIZARD_STEP_COUNT) as BrandWizardStep;
}

/** The step before `step` (step 1 has none). */
export function stepBefore(step: BrandWizardStep): BrandWizardStep {
  return Math.max(step - 1, 1) as BrandWizardStep;
}

/** A stored step number as a step (the API sends 1-8; anything else opens step 1). */
export function asWizardStep(n: number | null | undefined): BrandWizardStep {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= BRAND_WIZARD_STEP_COUNT ? (n as BrandWizardStep) : 1;
}

/** The step's pill label: Start, Company, Identity, Voice, Vocabulary, Guidelines, Audiences, Market. */
export function stepPill(step: BrandWizardStep): string {
  return BRAND_WIZARD_STEPS.find((s) => s.step === step)?.pill ?? '';
}

/** The step card's title. */
export function stepTitle(step: BrandWizardStep, mode: WizardMode): string {
  const section = BRAND_WIZARD_STEPS.find((s) => s.step === step)?.section;
  return section ? SECTION_COPY[section].title : WIZARD_COPY.step1Title[mode];
}

/** The step card's one-line description. */
export function stepDescription(step: BrandWizardStep, mode: WizardMode): string {
  const section = BRAND_WIZARD_STEPS.find((s) => s.step === step)?.section;
  return section ? SECTION_COPY[section].description : WIZARD_COPY.step1Description[mode];
}
